/* Geek Zone PR — "Orbit" draft behaviour (no dependencies, ~3 KB).
   Progressive enhancement only: every link, price and button works without it.
   - Hero: keyboard-only pause/resume (WCAG 2.2.2), pause when offscreen / tab hidden,
     pointer parallax (fine pointers only), ship leans toward the hovered or
     focused pillar.
   - One-shot scroll reveals for below-the-fold sections. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');

  function initHero(hero) {
    if (hero.dataset.gzInit) return;
    hero.dataset.gzInit = '1';
    if (hero.dataset.motion !== 'on' || reduce.matches) return;

    const btn = hero.querySelector('.gz-hero__pause');
    const label = btn && btn.querySelector('.gz-hero__pause-label');
    const setPaused = (p) => {
      hero.toggleAttribute('data-paused', p);
      if (btn) {
        btn.setAttribute('aria-pressed', String(p));
        if (label) label.textContent = p ? btn.dataset.playLabel : btn.dataset.pauseLabel;
      }
    };
    if (btn) btn.addEventListener('click', () => { const p = !hero.hasAttribute('data-paused'); setPaused(p); });

    // Pause every animation while the hero is offscreen or the tab is hidden.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => hero.toggleAttribute('data-offscreen', !e.isIntersecting)).observe(hero);
    }
    document.addEventListener('visibilitychange', () => hero.toggleAttribute('data-offscreen', document.hidden));

    if (!fine.matches) return;
    const wrap = hero.querySelector('.gz-shipwrap');
    if (!wrap) return;

    // Pointer parallax: one rAF write per frame, no layout reads in the loop.
    let raf = 0, nx = 0, ny = 0, rect = null;
    const measure = () => { rect = hero.getBoundingClientRect(); };
    hero.addEventListener('pointerenter', measure);
    window.addEventListener('resize', () => { rect = null; }, { passive: true });
    hero.addEventListener('pointermove', (ev) => {
      if (hero.hasAttribute('data-paused')) return;
      if (!rect) measure();
      nx = (ev.clientX - rect.left) / rect.width - 0.5;
      ny = (ev.clientY - rect.top) / rect.height - 0.5;
      if (!raf) raf = requestAnimationFrame(() => {
        raf = 0;
        wrap.style.setProperty('--gz-px', (nx * 18).toFixed(1) + 'px');
        wrap.style.setProperty('--gz-py', (ny * 12).toFixed(1) + 'px');
        hero.style.setProperty('--gz-star-x', (nx * -10).toFixed(1) + 'px');
      });
    });
    hero.addEventListener('pointerleave', () => {
      wrap.style.setProperty('--gz-px', '0px'); wrap.style.setProperty('--gz-py', '0px');
    });

    // Ship leans toward the pillar being considered (hover or keyboard focus).
    const lean = wrap.querySelector('.gz-shipwrap__lean');
    const pillars = [...hero.querySelectorAll('.gz-pillar')];
    const aim = (i) => {
      if (!lean) return;
      if (i < 0) { lean.style.removeProperty('--gz-lean-x'); lean.style.removeProperty('--gz-lean-r'); return; }
      const t = pillars.length > 1 ? i / (pillars.length - 1) - 0.5 : 0; // -0.5 … 0.5
      lean.style.setProperty('--gz-lean-x', (t * 140).toFixed(0) + 'px');
      lean.style.setProperty('--gz-lean-r', (t * 10).toFixed(1) + 'deg');
    };
    pillars.forEach((p, i) => {
      p.addEventListener('pointerenter', () => aim(i));
      p.addEventListener('pointerleave', () => aim(-1));
      p.addEventListener('focusin', () => aim(i));
      p.addEventListener('focusout', () => aim(-1));
    });
  }

  function initReveals(root) {
    if (reduce.matches || !('IntersectionObserver' in window)) return;
    const items = [...root.querySelectorAll('[data-gz-reveal]:not(.is-in)')];
    if (!items.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach((el) => {
      // Already on screen (e.g. reload mid-page): show immediately, no animation.
      const r = el.getBoundingClientRect();
      if (r.top < innerHeight && r.bottom > 0) el.classList.add('is-in');
      else io.observe(el);
    });
    root.classList.add('gz-reveal-ready');
  }

  function boot(scope) {
    scope.querySelectorAll('.gz-hero').forEach(initHero);
    scope.querySelectorAll('.gz[data-gz-reveals]').forEach(initReveals);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  // Theme editor: re-init a section when it is re-rendered.
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
